import { Box, Divider, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import {
  Accordion,
  Button,
  ButtonIcon,
  FormFeedback,
  Input,
  Select
} from '@morressier/alembic';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { CustomSearchSelect } from 'src/components/UserRegistrationSteps/StepThree/CustomSearchSelect';
import { IAdvancedSearchCriterion } from '@publishing-engine/domain/models';
import MultiValueInput, { normalizeValues } from './MultiValueInput';

/** One entry of the tenant's `advancedSearchFields` dashboard config. */
export type IAdvancedSearchField = {
  value: string;
  label: string;
  dataType: string;
  /** Present for option fields, which pick from a fixed set rather than typing. */
  options?: { value: string; label: string }[];
};

/**
 * A row's value is an array for fields that accept several values and a plain
 * string otherwise, matching the criterion the panel submits.
 */
type CriteriaRowValue = string | string[];

type CriteriaRow = { field: string; value: CriteriaRowValue };

interface AdvancedSearchForm {
  criteria: CriteriaRow[];
}

interface Props {
  fields: IAdvancedSearchField[];
  /** Criteria currently applied to the list; the panel edits a local copy. */
  value: IAdvancedSearchCriterion[];
  /** Called with the validated criteria when the user submits the panel. */
  onChange: (criteria: IAdvancedSearchCriterion[]) => void;
  onReset: () => void;
  /** Collapses the panel. Applied criteria are intentionally left in place. */
  onBack: () => void;
  isLoading?: boolean;
}

const MAX_CRITERIA = 5;

/**
 * Data types the backend matches exactly (as a number) rather than as a regex.
 * Values are still sent as strings — `submissionsFilterValidator` requires it.
 */
const NUMERIC_DATA_TYPES = new Set(['integer', 'number']);

/** Fields that accept several values, carried as an array and matched as "any of". */
const MULTI_VALUE_DATA_TYPES = new Set(['stringArray']);

/** Picks from a fixed set of options, so the backend matches the key exactly. */
const OPTION_DATA_TYPE = 'options';

const DATE_DATA_TYPE = 'date';

const emptyRow = (): CriteriaRow => ({ field: '', value: '' });

const findField = (fields: IAdvancedSearchField[], fieldValue?: string) =>
  fields.find(field => field.value === fieldValue);

/** The values of a row, whichever shape it currently holds. */
const rowValues = (value: CriteriaRowValue | undefined): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/** The value a row starts with once its field is chosen. */
const defaultValueForField = (dataType?: string): CriteriaRowValue =>
  dataType && MULTI_VALUE_DATA_TYPES.has(dataType) ? [] : '';

/**
 * A criterion reaches the panel from the URL or a saved filter, where it may
 * hold either shape. Coerce it to the one the field's control uses: an array
 * for multi-value fields, a plain string for everything else.
 */
const coerceRowValue = (
  value: CriteriaRowValue,
  dataType?: string
): CriteriaRowValue => {
  const values = normalizeValues(rowValues(value));

  if (dataType && MULTI_VALUE_DATA_TYPES.has(dataType)) {
    return values;
  }

  return values[0] ?? '';
};

/** Applied criteria -> editable rows, always leaving one blank row to type into. */
const toRows = (
  criteria: IAdvancedSearchCriterion[],
  fields: IAdvancedSearchField[]
): CriteriaRow[] =>
  criteria.length
    ? criteria.slice(0, MAX_CRITERIA).map(({ field, value }) => ({
        field,
        value: coerceRowValue(value, findField(fields, field)?.dataType)
      }))
    : [emptyRow()];

const AdvancedSearch = ({
  fields,
  value,
  onChange,
  onReset,
  onBack,
  isLoading
}: Props) => {
  const methods = useForm<AdvancedSearchForm>({
    defaultValues: { criteria: toRows(value, fields) }
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors }
  } = methods;

  const {
    fields: rows,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'criteria'
  });

  // `useFieldArray().fields` reflects array operations (append/remove) but not
  // the values the row Controllers write, so the selected field has to come from
  // the watched values — otherwise the per-field rules below never see a field
  // change and a numeric row would accept anything.
  const criteriaValues = useWatch({ control, name: 'criteria' });

  const [showNoCriteriaError, setShowNoCriteriaError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Re-sync when criteria are applied from outside (a saved filter, the URL on
  // load). Keying on the serialized value rather than the array identity keeps
  // this from firing on every render, and local edits only reach the parent on
  // submit — so typing is never clobbered.
  const serializedValue = JSON.stringify(value);
  useEffect(() => {
    const criteria: IAdvancedSearchCriterion[] = JSON.parse(serializedValue);
    reset({ criteria: toRows(criteria, fields) });
    setShowNoCriteriaError(false);
  }, [serializedValue, fields, reset]);

  // A saved filter may reference a field the current config no longer
  // advertises. Keep it selectable so the row renders and can be removed,
  // rather than silently leaving the user with an empty result set.
  const selectOptions = useMemo(() => {
    const options = fields.map(({ value: field, label }) => ({
      value: field,
      label
    }));
    const known = new Set(options.map(option => option.value));

    for (const criterion of value) {
      if (criterion.field && !known.has(criterion.field)) {
        known.add(criterion.field);
        options.push({ value: criterion.field, label: criterion.field });
      }
    }

    return options;
  }, [fields, value]);

  const isRowUntouched = (index: number) => {
    const row = methods.getValues(`criteria.${index}`);

    return !row?.field && rowValues(row?.value).every(value => !value.trim());
  };

  // Rules are per-row and tolerant of untouched rows: a row the user added but
  // never filled in must not block the search, while a half-filled row must.
  const getFieldRules = (index: number) => ({
    validate: (fieldValue: string) => {
      if (!fieldValue && isRowUntouched(index)) return true;

      return !!fieldValue || 'Please select search criteria';
    }
  });

  const getValueRules = (index: number, field?: IAdvancedSearchField) => ({
    validate: (inputValue: CriteriaRowValue) => {
      // A multi-value row holds several values; validating the row means
      // validating the one value each control wrote into it.
      const values = normalizeValues(rowValues(inputValue));
      if (!values.length) {
        return isRowUntouched(index) ? true : 'Please enter value';
      }

      if (!field || !NUMERIC_DATA_TYPES.has(field.dataType)) return true;

      for (const value of values) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return 'Please enter a valid number';
        if (field.dataType === 'integer' && !Number.isInteger(parsed)) {
          return 'Please enter a whole number';
        }
      }

      return true;
    }
  });

  const onSubmit = handleSubmit(data => {
    const criteria = data.criteria
      .map(row => {
        const values = normalizeValues(rowValues(row.value));

        return {
          field: row.field,
          // Multi-value fields keep the array, so the backend can match any of
          // them; every other field sends its single value.
          value: Array.isArray(row.value) ? values : values[0] ?? ''
        };
      })
      .filter(
        row =>
          row.field && (Array.isArray(row.value) ? row.value.length : row.value)
      );

    if (criteria.length === 0) {
      setShowNoCriteriaError(true);
      return;
    }

    setShowNoCriteriaError(false);
    onChange(criteria);
  });

  const handleReset = () => {
    reset({ criteria: [emptyRow()] });
    setShowNoCriteriaError(false);
    onReset();
  };

  const isMaxCriteriaReached = rows.length >= MAX_CRITERIA;

  // Applied criteria, not the rows being edited — the count in the header should
  // describe the search that is actually running. A multi-value criterion counts
  // as one, and an empty array counts as none.
  const appliedCount = value.filter(criterion =>
    Array.isArray(criterion.value)
      ? criterion.value.length > 0
      : !!criterion.value
  ).length;

  return (
    <Accordion
      chakraAccordionProps={{
        allowToggle: true,
        // Collapsing hides the criteria rows and nothing else: the search stays
        // in advanced mode with its criteria applied, and only "Back to Normal
        // Search" leaves it.
        index: isExpanded ? [0] : [],
        onChange: (index: number[]) => setIsExpanded(index.includes(0))
      }}
      items={[
        {
          accordionItemProps: { marginBottom: 0.5 },
          title: (
            <Text textStyle="headingItem">
              Advanced Search
              {appliedCount > 0 && ` (${appliedCount})`}
            </Text>
          ),
          content: (
            <Flex direction="column" gap={2}>
              {rows.map((row, index) => {
                const selectedField = findField(
                  fields,
                  criteriaValues?.[index]?.field
                );

                return (
                  <Box key={row.id}>
                    <Grid
                      templateColumns="30% 1fr auto auto"
                      columnGap={4}
                      rowGap={2}
                      alignItems="flex-start"
                    >
                      <GridItem colSpan={1}>
                        <Controller
                          control={control}
                          name={`criteria.${index}.field`}
                          rules={getFieldRules(index)}
                          render={({ field, fieldState }) => (
                            <CustomSearchSelect
                              value={field.value}
                              onChange={(val: string) => {
                                if (val === field.value) return;

                                field.onChange(val);
                                // The previous value belongs to the previous
                                // field, and so does its shape — a chips field
                                // starts empty rather than as a blank string.
                                setValue(
                                  `criteria.${index}.value`,
                                  defaultValueForField(
                                    findField(fields, val)?.dataType
                                  ),
                                  { shouldValidate: false }
                                );
                                clearErrors(`criteria.${index}.value`);
                                setShowNoCriteriaError(false);
                              }}
                              options={selectOptions}
                              placeholder="criteria"
                              isInvalid={!!fieldState.error}
                            />
                          )}
                        />
                      </GridItem>

                      <GridItem colSpan={1}>
                        <Controller
                          control={control}
                          name={`criteria.${index}.value`}
                          rules={getValueRules(index, selectedField)}
                          render={({ field, fieldState }) => {
                            // A shared clear-and-forget for whichever control
                            // renders below.
                            const handleChange = (
                              nextValue: CriteriaRowValue
                            ) => {
                              field.onChange(nextValue);
                              clearErrors(`criteria.${index}.value`);
                              setShowNoCriteriaError(false);
                            };

                            // Remounting on a field change keeps a value typed
                            // for one field out of a control of another shape.
                            const controlKey = `value-${index}-${
                              selectedField?.value ?? 'empty'
                            }`;
                            const isInvalid = !!fieldState.error;

                            if (
                              selectedField &&
                              MULTI_VALUE_DATA_TYPES.has(selectedField.dataType)
                            ) {
                              return (
                                <MultiValueInput
                                  key={controlKey}
                                  value={rowValues(field.value)}
                                  onChange={handleChange}
                                  isInvalid={isInvalid}
                                />
                              );
                            }

                            if (
                              selectedField?.dataType === OPTION_DATA_TYPE &&
                              selectedField.options?.length
                            ) {
                              return (
                                <Select
                                  key={controlKey}
                                  value={rowValues(field.value)[0] ?? ''}
                                  onChange={event =>
                                    handleChange(event.target.value)
                                  }
                                  invalid={isInvalid}
                                >
                                  <option value="" disabled>
                                    Select value
                                  </option>
                                  {selectedField.options.map(option => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              );
                            }

                            const isNumeric =
                              !!selectedField &&
                              NUMERIC_DATA_TYPES.has(selectedField.dataType);

                            return (
                              <Input
                                key={controlKey}
                                autoComplete="off"
                                name={`criteria.${index}.value`}
                                type={
                                  selectedField?.dataType === DATE_DATA_TYPE
                                    ? 'date'
                                    : isNumeric
                                    ? 'number'
                                    : 'text'
                                }
                                step={
                                  selectedField?.dataType === 'integer'
                                    ? 1
                                    : 'any'
                                }
                                placeholder="Enter search value"
                                value={rowValues(field.value)[0] ?? ''}
                                onChange={event =>
                                  handleChange(event.target.value)
                                }
                                invalid={isInvalid}
                              />
                            );
                          }}
                        />
                      </GridItem>

                      <GridItem>
                        <ButtonIcon
                          ariaLabel="Add Criteria"
                          icon={<FontAwesomeIcon icon={faPlus} />}
                          onClick={() =>
                            !isMaxCriteriaReached && append(emptyRow())
                          }
                          disabled={isMaxCriteriaReached}
                        />
                      </GridItem>

                      <GridItem>
                        <ButtonIcon
                          ariaLabel="Remove Criteria"
                          icon={<FontAwesomeIcon icon={faTrashAlt} />}
                          onClick={() => remove(index)}
                          disabled={rows.length === 1}
                        />
                      </GridItem>

                      <GridItem colSpan={1}>
                        {errors?.criteria?.[index]?.field && (
                          <FormFeedback variant="error">
                            {errors.criteria[index]?.field?.message}
                          </FormFeedback>
                        )}
                      </GridItem>

                      <GridItem colSpan={1}>
                        {errors?.criteria?.[index]?.value && (
                          <FormFeedback variant="error">
                            {errors.criteria[index]?.value?.message}
                          </FormFeedback>
                        )}
                      </GridItem>
                    </Grid>
                  </Box>
                );
              })}

              {isMaxCriteriaReached && (
                <Text textStyle="textContext">
                  You can add up to {MAX_CRITERIA} search criteria.
                </Text>
              )}

              {showNoCriteriaError && (
                <FormFeedback variant="error">
                  Please select at least one search criteria
                </FormFeedback>
              )}

              <Divider />

              <Flex justifyContent="space-between" alignItems="center" mt={1}>
                <Button onClick={onBack} variant="secondary">
                  Back to Normal Search
                </Button>

                <Flex gap={2}>
                  <Button variant="secondary" onClick={handleReset}>
                    Reset
                  </Button>
                  <Button
                    variant="primary"
                    type="button"
                    onClick={event => {
                      event.preventDefault();
                      onSubmit();
                    }}
                    isLoading={isLoading}
                    loadingText="Searching..."
                  >
                    Search
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          )
        }
      ]}
    />
  );
};

export default AdvancedSearch;
